import React, { useState } from "react";
import { Box, Button, Link, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { GWORKSPACE_DOMAIN_WIDE_DELEGATION_URL } from "../../utils/constants";

export type GWorkspaceOAuthScopeCheck = {
  scope: string;
  status: string;
  detail?: string;
};

const GWorkspaceOAuthScopeChecksResultsBox: React.FC<{
  checks: GWorkspaceOAuthScopeCheck[];
}> = ({ checks }) => {
  const [copied, setCopied] = useState(false);
  if (!checks?.length) return null;

  const failedChecks = checks.filter(
    (check) => String(check.status).toLowerCase() !== "success"
  );
  const passedCount = checks.length - failedChecks.length;

  const copyMissingScopes = async () => {
    try {
      await navigator.clipboard.writeText(
        failedChecks.map((check) => check.scope).join(",")
      );
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        border: "1px solid #e5e5e5",
        borderRadius: 1,
        bgcolor: "#fff",
      }}
    >
      <Typography
        sx={{
          fontFamily: "var(--theo-font-sans)",
          fontWeight: 600,
          fontSize: 13,
          color: "#1a1a1a",
          mb: 0.75,
        }}
      >
        OAuth scope checks
      </Typography>

      {failedChecks.length === 0 ? (
        <Typography
          sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 12, color: "#16a34a" }}
        >
          All {checks.length} required OAuth scopes are authorized.
        </Typography>
      ) : (
        <>
          <Typography
            sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 12, color: "#4b5563", mb: 1 }}
          >
            {passedCount} authorized · {failedChecks.length} missing
          </Typography>
          <Box
            sx={{
              p: 1,
              mb: 1,
              bgcolor: "#fff8f8",
              border: "1px solid #fecaca",
              borderRadius: 1,
            }}
          >
            <Typography
              sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 12, color: "#4b5563", lineHeight: 1.5 }}
            >
              1. Open{" "}
              <Link
                href={GWORKSPACE_DOMAIN_WIDE_DELEGATION_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "#705d4a", fontWeight: 600 }}
              >
                Manage Domain-Wide Delegation
              </Link>
              .
            </Typography>
            <Typography
              sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 12, color: "#4b5563", lineHeight: 1.5 }}
            >
              2. Edit the service account Client ID and add the missing scopes below.
            </Typography>
            <Typography
              sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 12, color: "#4b5563", lineHeight: 1.5 }}
            >
              3. Click Authorize, wait a few minutes for propagation, then click Test again.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={copyMissingScopes}
              startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
              sx={{
                mt: 1,
                fontFamily: "var(--theo-font-sans)",
                textTransform: "none",
                borderColor: copied ? "#16a34a" : "#705d4a",
                color: copied ? "#16a34a" : "#705d4a",
              }}
            >
              {copied ? "Copied" : "Copy missing scopes"}
            </Button>
          </Box>

          {failedChecks.map((check) => (
            <Box
              key={check.scope}
              sx={{
                py: 0.6,
                borderBottom: "1px solid #f0f0f0",
                "&:last-child": { borderBottom: "none" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    mt: 0.5,
                    borderRadius: "50%",
                    bgcolor: "#dc2626",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "monospace",
                    fontSize: 11,
                    color: "#333",
                    lineHeight: 1.4,
                    wordBreak: "break-all",
                    flex: 1,
                  }}
                >
                  {check.scope}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "var(--theo-font-sans)",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#dc2626",
                  }}
                >
                  Failed
                </Typography>
              </Box>
              {check.detail ? (
                <Typography
                  sx={{
                    pl: 2,
                    mt: 0.25,
                    fontFamily: "var(--theo-font-sans)",
                    fontSize: 11,
                    color: "#6b7280",
                  }}
                >
                  {check.detail}
                </Typography>
              ) : null}
            </Box>
          ))}
        </>
      )}
    </Box>
  );
};

export default GWorkspaceOAuthScopeChecksResultsBox;
